/**
 * Control Panel — Trace's metadata settings page.
 *
 * Two tabs:
 *   1. Brands           — display name, icon, manager email, store URLs per platform
 *   2. Platform Icons   — channels (Amazon/Flipkart/etc) + their icons
 *
 * Metadata-only: nothing here touches ad_properties_data. Filters and tables
 * across the app can read from these collections to show icons + store links.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Download, X, Package, Layers, Globe, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ControlPanel() {
  const [tab, setTab] = useState('brands');
  const [headerFilters, setHeaderFilters] = useState({});
  return (
    <div className="flex min-h-screen bg-[#F6F6F7]">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header filters={headerFilters} onFiltersChange={setHeaderFilters} hideGlobalFilters hideComparison />
        <main className="flex-1 max-w-[1400px] mx-auto w-full px-6 py-6">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-[#0F0F0F]">Control Panel</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage brand and platform metadata used across Trace filters and tables.</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4 border-b border-gray-200">
            {[
              { id: 'brands',    label: 'Brands',         icon: Package },
              { id: 'platforms', label: 'Platform Icons', icon: Layers },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`cp-tab-${t.id}`}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                  tab === t.id ? 'border-[#2EB4AC] text-[#0F0F0F]' : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </button>
            ))}
          </div>

          {tab === 'brands' && <BrandsTab />}
          {tab === 'platforms' && <PlatformsTab />}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BRANDS TAB
// ---------------------------------------------------------------------------
function BrandsTab() {
  const [rows, setRows] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [editing, setEditing] = useState(null); // brand object or {} for new
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const [b, p] = await Promise.all([
        fetch(`${API}/control-panel/brands`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/control-panel/platforms`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setRows(b.brands || []);
      setPlatforms(p.platforms || []);
    } catch (e) { toast.error('Failed to load brands'); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setRows(prev => prev.filter(r => r.brand_id !== id)); // optimistic
    try {
      const r = await fetch(`${API}/control-panel/brands/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Brand deleted');
    } catch { toast.error('Delete failed'); load(); }
    setConfirmDelete(null);
  };

  const exportCsv = () => { window.open(`${API}/control-panel/brands/export`, '_blank'); };

  return (
    <Card
      title="Brands"
      description="Manage brand options for filters, tables, and pickers."
      actions={
        <div className="flex gap-2">
          <button onClick={exportCsv} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-1.5" data-testid="cp-brands-export">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => setEditing({})} className="px-3 py-1.5 text-sm bg-[#2EB4AC] hover:bg-[#26a19a] text-white rounded-lg flex items-center gap-1.5" data-testid="cp-brands-add">
            <Plus className="w-4 h-4" /> Add Brand
          </button>
        </div>
      }
    >
      {rows === null ? (
        <LoadingRow />
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No brands yet. Click "Add Brand" to create one.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {rows.map(b => (
            <div key={b.brand_id} className="flex items-center gap-3 py-3" data-testid={`cp-brand-row-${b.brand_id}`}>
              <IconPreview url={b.icon_url} fallback={<Package className="w-4 h-4 text-gray-400" />} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0F0F0F] truncate">
                  {b.name} {b.manager_email && <span className="text-xs text-gray-500 font-normal">({b.manager_email})</span>}
                </div>
                {b.store_urls && Object.keys(b.store_urls).length > 0 && (
                  <div className="text-[11px] text-gray-500 mt-0.5">{Object.keys(b.store_urls).filter(k => b.store_urls[k]).length} store URL(s) configured</div>
                )}
              </div>
              <button onClick={() => setEditing(b)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500 flex items-center justify-center" data-testid={`cp-brand-edit-${b.brand_id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => setConfirmDelete(b)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 flex items-center justify-center" data-testid={`cp-brand-delete-${b.brand_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <BrandModal
          initial={editing}
          platforms={platforms}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={`Delete "${confirmDelete.name}"?`}
          body="This only removes the brand from the picker; your analytics data is untouched."
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove(confirmDelete.brand_id)}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// PLATFORMS TAB
// ---------------------------------------------------------------------------
function PlatformsTab() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      const d = await fetch(`${API}/control-panel/platforms`, { credentials: 'include' }).then(r => r.json());
      setRows(d.platforms || []);
    } catch { toast.error('Failed to load platforms'); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setRows(prev => prev.filter(r => r.platform_id !== id));
    try {
      const r = await fetch(`${API}/control-panel/platforms/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Platform deleted');
    } catch { toast.error('Delete failed'); load(); }
    setConfirmDelete(null);
  };

  return (
    <Card
      title="Platforms & Icons"
      description="Manage platforms and their icons. Changes apply everywhere across the app."
      actions={
        <button onClick={() => setEditing({})} className="px-3 py-1.5 text-sm bg-[#2EB4AC] hover:bg-[#26a19a] text-white rounded-lg flex items-center gap-1.5" data-testid="cp-plat-add">
          <Plus className="w-4 h-4" /> Add Platform
        </button>
      }
    >
      {rows === null ? (
        <LoadingRow />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(p => (
            <div key={p.platform_id} className="flex items-center gap-3 border border-gray-200 rounded-xl p-3" data-testid={`cp-plat-row-${p.platform_id}`}>
              <IconPreview url={p.icon_url} fallback={<Globe className="w-4 h-4 text-gray-400" />} size="lg" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#0F0F0F] truncate">{p.name}</div>
                <div className="text-[11px] text-gray-500">{p.icon_url ? 'Has icon' : 'No icon'}</div>
              </div>
              <button onClick={() => setEditing(p)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500 flex items-center justify-center" data-testid={`cp-plat-edit-${p.platform_id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => setConfirmDelete(p)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 flex items-center justify-center" data-testid={`cp-plat-delete-${p.platform_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing !== null && (
        <PlatformModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirmDelete && (
        <ConfirmModal
          title={`Delete "${confirmDelete.name}"?`}
          body="This removes the platform metadata. Analytics rows tagged with this channel are untouched."
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove(confirmDelete.platform_id)}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Shared UI
// ---------------------------------------------------------------------------
function Card({ title, description, actions, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-[#0F0F0F]">{title}</h2>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

function LoadingRow() {
  return <div className="flex items-center justify-center py-8 text-gray-400 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
}

function IconPreview({ url, fallback, size = 'md' }) {
  const dim = size === 'lg' ? 'w-10 h-10' : 'w-8 h-8';
  if (!url) return <div className={`${dim} rounded-lg bg-gray-100 flex items-center justify-center shrink-0`}>{fallback}</div>;
  return (
    <div className={`${dim} rounded-lg bg-white border border-gray-100 flex items-center justify-center overflow-hidden shrink-0`}>
      <img src={url} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
    </div>
  );
}

function Modal({ title, children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="cp-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[560px] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-[#0F0F0F]">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-500 flex items-center justify-center" data-testid="cp-modal-close"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BrandModal({ initial, platforms, onClose, onSaved }) {
  const isEdit = !!initial.brand_id;
  const [name, setName] = useState(initial.name || '');
  const [iconUrl, setIconUrl] = useState(initial.icon_url || '');
  const [email, setEmail] = useState(initial.manager_email || '');
  const [storeUrls, setStoreUrls] = useState(initial.store_urls || {});
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Brand name is required'); return; }
    setBusy(true);
    try {
      const url = isEdit ? `${API}/control-panel/brands/${initial.brand_id}` : `${API}/control-panel/brands`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), icon_url: iconUrl.trim(), manager_email: email.trim(), store_urls: storeUrls }),
      });
      if (!r.ok) { toast.error((await r.json()).detail || 'Save failed'); return; }
      toast.success(isEdit ? 'Brand updated' : 'Brand created');
      onSaved();
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  return (
    <Modal title={isEdit ? 'Edit Brand' : 'Add Brand'} onClose={onClose}>
      <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">
        <Field label="Brand Name *" testid="cp-brand-name">
          <input value={name} onChange={e => setName(e.target.value)} className={inputCls} placeholder="e.g. Osmo" data-testid="cp-brand-name-input" />
        </Field>
        <Field label="Icon URL (optional)">
          <input value={iconUrl} onChange={e => setIconUrl(e.target.value)} className={inputCls} placeholder="https://..." data-testid="cp-brand-icon-input" />
          {iconUrl && (
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <span>Preview:</span>
              <IconPreview url={iconUrl} fallback={<Package className="w-4 h-4 text-gray-400" />} />
            </div>
          )}
        </Field>
        <Field label="Brand Manager Email">
          <div className="relative">
            <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={email} onChange={e => setEmail(e.target.value)} className={inputCls + ' pl-9'} placeholder="manager@company.com" data-testid="cp-brand-email-input" />
          </div>
          <p className="text-[11px] text-gray-400 mt-1">Auto-filled as notification recipient when this brand is selected</p>
        </Field>
        {platforms.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5 mb-2"><Globe className="w-4 h-4" /> Brand Store URLs (by Platform)</label>
            <p className="text-[11px] text-gray-500 mb-2">Add the store page URL for this brand on each platform.</p>
            <div className="space-y-2">
              {platforms.map(p => (
                <div key={p.platform_id} className="flex items-center gap-2">
                  <IconPreview url={p.icon_url} fallback={<Globe className="w-3.5 h-3.5 text-gray-400" />} />
                  <div className="text-xs w-28 truncate">{p.name}</div>
                  <input
                    value={storeUrls[p.name] || ''}
                    onChange={e => setStoreUrls(s => ({ ...s, [p.name]: e.target.value }))}
                    className={inputCls + ' text-xs'}
                    placeholder={`https://www.${p.name.toLowerCase().replace(/\s+/g, '')}.com/store/...`}
                    data-testid={`cp-brand-store-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button onClick={save} disabled={busy} className="px-4 py-1.5 text-sm bg-[#2EB4AC] hover:bg-[#26a19a] disabled:bg-gray-300 text-white rounded-lg flex items-center gap-1.5" data-testid="cp-brand-save">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
        </button>
      </div>
    </Modal>
  );
}

function PlatformModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.platform_id;
  const [name, setName] = useState(initial.name || '');
  const [iconUrl, setIconUrl] = useState(initial.icon_url || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Platform name is required'); return; }
    setBusy(true);
    try {
      const url = isEdit ? `${API}/control-panel/platforms/${initial.platform_id}` : `${API}/control-panel/platforms`;
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim(), icon_url: iconUrl.trim() }),
      });
      if (!r.ok) { toast.error((await r.json()).detail || 'Save failed'); return; }
      toast.success(isEdit ? 'Platform updated' : 'Platform created');
      onSaved();
    } catch { toast.error('Save failed'); } finally { setBusy(false); }
  };

  const reset = () => setIconUrl('');

  return (
    <Modal title={isEdit ? `Edit Icon — ${initial.name}` : 'Add Platform'} onClose={onClose}>
      <div className="px-5 py-4 space-y-4">
        <div className="flex items-center justify-center py-4">
          <IconPreview url={iconUrl} fallback={<Globe className="w-8 h-8 text-gray-300" />} size="lg" />
        </div>
        {!isEdit && (
          <Field label="Platform Name *">
            <input value={name} onChange={e => setName(e.target.value)} className={inputCls} placeholder="e.g. Amazon" data-testid="cp-plat-name-input" />
          </Field>
        )}
        <Field label="Icon URL">
          <input value={iconUrl} onChange={e => setIconUrl(e.target.value)} className={inputCls} placeholder="https://..." data-testid="cp-plat-icon-input" />
          <p className="text-[11px] text-gray-400 mt-1">Enter a URL to an image file (SVG, PNG, or ICO recommended)</p>
        </Field>
        {isEdit && iconUrl && (
          <button onClick={reset} className="text-xs text-red-600 hover:text-red-700 border border-red-200 rounded-lg px-2 py-1" data-testid="cp-plat-reset">Reset to Default</button>
        )}
      </div>
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
        <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button onClick={save} disabled={busy} className="px-4 py-1.5 text-sm bg-[#2EB4AC] hover:bg-[#26a19a] disabled:bg-gray-300 text-white rounded-lg flex items-center gap-1.5" data-testid="cp-plat-save">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save Icon
        </button>
      </div>
    </Modal>
  );
}

function ConfirmModal({ title, body, onCancel, onConfirm }) {
  return (
    <Modal title={title} onClose={onCancel}>
      <div className="px-5 py-4 text-sm text-gray-700">{body}</div>
      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
        <button onClick={onConfirm} className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg" data-testid="cp-confirm-delete">Delete</button>
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-700 block mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2EB4AC] focus:ring-2 focus:ring-[#2EB4AC]/10';
